import type {CheckoutBrand} from '@wix/ambassador-ecom-v1-ecommerce-settings/types';
import {EcommerceSettingsModel} from '../domain/models/checkout/EcommerceSettings.model';
import {LoadingStateEcommerceSettingsModel} from '../domain/models/checkout/LoadingStateEcommerceSettings.model';
import {PolicyButtonLocation, PolicyType} from '../common/components/PolicyButtonWithDialog/PolicyButtonWithDialog';
import {ITrackEventName, ITrackEventParams} from '@wix/native-components-infra';
import {SlotId} from '../domain/utils/slotId';
import {PaymentsWidgetConfiguration} from '@wix/cashier-payments-widget/dist/src/types/PaymentsWidgetConfiguration';
import {PaymentsWidgetProps} from '@wix/cashier-payments-widget/dist/src/types/PaymentsWidgetProps';
import {MemberAddressesInfoModel} from '../domain/models/checkout/MemberAddressesInfo.model';
import {PaymentMethod} from '@wix/cashier-payments-widget';
import {CashierMandatoryFieldsOverrides, PaymentError} from './payment.types';
import {AddressWithContactModel} from '../domain/models/checkout/Cart/AddressWithContact.model';
import {BiAddressActionOrigin, BiMobilePosition, CheckoutLoadedWithErrorReason} from '../domain/utils/bi.util';
import {ViolationModel} from '../domain/models/checkout/CartSummary/Violation.model';
import {confirmPayment} from '@wix/ecom-platform-express-checkout-buttons';
import {DataExtendedFieldView} from '@wix/wixstores-client-storefront-sdk';
import {
  checkoutOpenCloseYourOrderSummaryInMobileParams,
  checkoutShowHideItemsSectionInMobileViewParams,
} from '@wix/bi-logger-ecom-platform-data/v2/types';
import {FormError, FormOverride} from '@wix/form-viewer/dist/types/types';
import {FormControllerAPI} from '@wix/form-viewer';
import {CheckoutContentModel} from '../domain/models/checkout/CheckoutContent.model';
import {SuggestedFix} from '../domain/models/checkout/CartSummary/ViolationLineItemTargetModel.model';
import {ComponentState} from '@wix/delivery-section-checkout/types';
import {ErrorCodesMapConditionalEnforcement} from '@wix/error-handler';
import {ControllerParams, HttpResponse} from '@wix/yoshi-flow-editor';
import {
  GetCheckoutContentApplicationErrors,
  GetCheckoutContentResponse,
} from '@wix/ambassador-ecom-api-v1-checkout-content-provider/types';
import {
  ChargeOrderPaymentRequestApplicationErrors,
  ChargeOrderPaymentRequestResponse,
  GetOrderPaymentRequestApplicationErrors,
  GetOrderPaymentRequestResponse,
} from '@wix/ambassador-ecom-v1-order-payment-request/types';
import {CartModel} from '../domain/models/checkout/Cart/Cart.model';
import {CartSummaryModel} from '../domain/models/checkout/CartSummary/CartSummary.model';
import {ContactModel} from '../domain/models/checkout/Cart/Contact.model';
import {AddressModel} from '../domain/models/checkout/Cart/Address.model';
import {CheckoutOrigin} from '../domain/utils/common/bi.util.common';
import {PlaceOrderResponseModel} from '../domain/models/checkout/PlaceOrderResponse.model';
import {PurchaseRecommendationModel} from '../domain/models/checkout/PurchaseRecommendation.model';
import {AddCouponErrorModel} from '../domain/models/checkout/Errors/AddCouponError.model';
import {AddGiftCardErrorModel} from '../domain/models/checkout/Errors/AddGiftCardError.model';
import {ILocaleKeys} from '../locale-keys/LocaleKeys';
import {ModalErrorModel} from '../domain/models/checkout/Errors/ModalError.model';

export enum SkipCheckoutFlowStatus {
  CheckingEligibility = 'CheckingEligibility',
  PlacingOrder = 'PlacingOrder',
  None = 'None',
}

export type BillingUpdateParams = {
  billingAddress?: AddressModel;
  billingContact?: ContactModel;
  billingAddressId?: string;
  email?: string;
};

export type CustomerDetailsUpdateParams = {
  customerContact?: ContactModel;
  email?: string;
  deliveryAddress?: AddressModel;
  deliveryAddressId?: string;
  customFieldValue?: string;
  extendedFieldsValue?: any;
};

export declare type OnlyFunctionProps<O> = {
  // eslint-disable-next-line @typescript-eslint/ban-types
  [K in keyof O]: O[K] extends Function ? K : never;
}[keyof O];

export type CheckoutControllerProps = {
  isOneColumnView?: boolean;
  isLoading: boolean;
  isHeaderHidden?: boolean;
  failedToFetch?: boolean;
  skipCheckoutFlowStatus: SkipCheckoutFlowStatus;
  checkoutStore: CheckoutStoreProps;
  checkoutSettingsStore: CheckoutSettingsStoreProps;
  loadingStateEcommerceSettingsStore: LoadingStateEcommerceSettingsStoreProps;
  navigationStore: NavigationStoreProps;
  slotsStore: SlotStoreProps;
  checkboxesStore: CheckboxesStoreProps;
  stepsManagerStore: StepsManagerStoreProps;
  paymentStore: PaymentStoreProps;
  formsStore: FormsStoreProps;
  memberStore: MemberStoreProps;
  deliveryStore: DeliveryStoreProps;
  isPoliciesPage?: boolean;
};

export type PoliciesControllerProps = {
  isLoading: boolean;
  isPoliciesPage?: boolean;
  checkoutSettingsStore: PoliciesCheckoutSettingsStoreProps;
  focusedPolicyId?: string;
};

export interface PoliciesCheckoutSettingsStoreProps {
  ecommerceSettings: EcommerceSettingsModel;
  loadingStateEcommerceSettings: LoadingStateEcommerceSettingsModel;
}

export interface IBusinessStore {
  init?: () => Promise<void> | void;
  toProps?: () => Record<string, any>;
  propsKey: string;
}

export interface CheckoutStoreProps {
  cart: CartModel;
  cartSummary?: CartSummaryModel;
  extendedFieldsView: DataExtendedFieldView[];
  setPlaceOrderPaymentError: (paymentError: PaymentError, errorCode: string) => void;
  applyCouponError?: AddCouponErrorModel;
  applyGiftCardError?: AddGiftCardErrorModel;
  modalError?: ModalErrorModel;
  shouldShowPreviewModalExpress: boolean;
  onInvalidDetailsFormSubmit: () => void;
  isPlaceOrderButtonDisabled: boolean;
  hasNoPaymentMethods: boolean;
  setHasNoPaymentMethods: (value: boolean) => void;
  shouldRequireZipCode: boolean;
  shouldCollectPayment: boolean;
  isInAppPurchaseFlow: boolean;
  iapProductIds: string[];
  shouldShowSubdivisionSelector: boolean;
  isFastFlow: boolean;
  isPickupFlow: boolean;
  isShippingFlow: boolean;
  isCartSummaryUpdating: boolean;
  isSelectedDeliveryMethodInvalid: boolean;
  hasCalculateCartError: boolean;
  onRefreshCheckout: () => Promise<void>;
  refetchCartSummary: () => Promise<void>;
  applyCoupon: (couponCode: string, mobilePosition?: string) => Promise<void>;
  removeCoupon: (mobilePosition?: string) => Promise<void>;
  applyGiftCard: (giftCardCode: string, mobilePosition?: string) => Promise<void>;
  removeGiftCard: (giftCardId: string, mobilePosition?: string) => Promise<void>;
  setFastFlowFormFields: (extendedFieldsValue: any, customFieldValue?: string) => Promise<void>;
  removeLineItem: (lineItemId: string) => Promise<void>;
  clickPlaceOrderButton: (
    subscriptionCheckboxesChecked: {unified: boolean; email: boolean; sms: boolean},
    detailsId?: string
  ) => Promise<PlaceOrderResponseModel | undefined>;
  setPaymentAndBillingDetails: ({
    billingContact,
    billingAddress,
    billingAddressId,
    activePaymentId,
    email,
  }: {
    billingContact?: ContactModel;
    billingAddress?: AddressModel;
    billingAddressId?: string;
    activePaymentId?: string;
    email?: string;
  }) => Promise<boolean>;
  setBillingDetails: ({
    billingContact,
    billingAddress,
    billingAddressId,
    setAsDefault,
  }: {
    billingContact?: ContactModel;
    billingAddress?: AddressModel;
    billingAddressId?: string;
    setAsDefault?: boolean;
  }) => Promise<void>;
  submitCustomerDetails: ({
    customerContact,
    email,
    customFieldValue,
    extendedFieldsValue,
    deliveryAddress,
    deliveryAddressId,
    setAsDefault,
  }: {
    customerContact?: ContactModel;
    email?: string;
    customFieldValue?: string;
    deliveryAddress?: AddressModel;
    deliveryAddressId?: string;
    extendedFieldsValue?: any;
    setAsDefault?: boolean;
  }) => Promise<void>;
  onHideBillingDetails: () => void;
  setSelectedPaymentMethod: () => Promise<void>;
  setZipCode: (zipCodeValue: string) => Promise<void>;
  onAddCouponSectionOpen: (mobilePosition?: string) => void;
  onAddGiftCardSectionOpen: (mobilePosition?: string) => void;
  onErrorDialogClosed: () => void;
  onErrorDialogOpened: (error?: ModalErrorModel) => void;
  onPolicyClicked: (linkLocation: PolicyButtonLocation, policyType: PolicyType) => void;
  onMobileFoldableSummaryToggle: (
    mobilePosition: BiMobilePosition,
    partialParams:
      | Partial<checkoutOpenCloseYourOrderSummaryInMobileParams>
      | Partial<checkoutShowHideItemsSectionInMobileViewParams>
  ) => void;
  setSubdivision: (subdivisionValue: string) => Promise<void>;
  debouncedUpdateBilling: (params: BillingUpdateParams) => void;
  debouncedUpdateCustomerDetails: (params: CustomerDetailsUpdateParams) => void;
  shouldShowGiftCardSection: boolean;
  shouldShowCouponSection: boolean;
  onLogoutDialogToggled: (isOpen: boolean) => void;
  shouldShowViolations: boolean;
  shouldShowDiscounts: boolean;
  defaultViolationsToDisplay: ViolationModel[];
  isPickupButShippingFlow: boolean;
  AddShippingAddressToPreselectedFlow: () => void;
  isCouponUpdating: boolean;
  isGiftCardUpdating: boolean;
  checkoutPageFinishLoading: (params: {hasPremiumError: boolean; cannotShip?: boolean}) => void;
  reportViolationRemoveButtonClick: ({
    message,
    lineItemId,
    suggestedFix,
  }: {
    message?: string;
    lineItemId?: string;
    suggestedFix?: SuggestedFix;
  }) => void;
  reportClickToOpenCloseMoreDetails: ({
    lineItemId,
    action,
    origin,
  }: {
    lineItemId?: string;
    action: string;
    origin?: CheckoutOrigin;
  }) => void;
  reportClickOnSameAsDeliveryAddressCheckbox: ({action}: {action: string}) => void;
  reportCheckoutCartClickOnCtaInErrorDialog: ({errorCode, actionName}: {errorCode: string; actionName: string}) => void;
  reportCheckoutErrorCtaClicked: (reason: CheckoutLoadedWithErrorReason, buttonName: string) => void;
  reportPaymentStepLoadedWithError: () => void;
  reportCheckoutBillingAddressPresentedToVisitor: ({overrides}: {overrides: BillingFormOverrides}) => void;
  onDeliveryMethodContinue: () => Promise<void>;
  purchaseRecommendations: PurchaseRecommendationModel[];
  arePurchaseRecommendationsLoading: boolean;
  applyRecommendation: (recommendation: PurchaseRecommendationModel, position: number) => Promise<void>;
  isApplyingRecommendation: boolean;
}

export interface PaymentStoreProps {
  cashierWidgetProps: PaymentsWidgetProps;
  cashierConfiguration: PaymentsWidgetConfiguration;
  cashierMandatoryFields: CashierMandatoryFieldsOverrides;
  confirmPayment: (arg: {chargeResponseToken: string; newApi?: boolean}) => ReturnType<typeof confirmPayment>;
  setCashierMandatoryFields: (cashierMandatoryFields: CashierMandatoryFieldsOverrides) => void;
  isExpressEmpty: boolean;
}

export interface FormsStoreObservedProps {
  validateBillingAddress: (
    addressWithContact: AddressWithContactModel,
    options?: {cashierMandatoryFields?: CashierMandatoryFieldsOverrides; ignoreEmptyFields?: boolean}
  ) => Promise<boolean>;
  validateBillingAddressForPartialUpdate: (
    addressWithContact: AddressWithContactModel,
    options?: {cashierMandatoryFields?: CashierMandatoryFieldsOverrides; ignoreEmptyFields?: boolean}
  ) => Promise<boolean>;
  validateShippingAddress: (addressWithContact: AddressWithContactModel) => Promise<boolean>;
  validateShippingAddressForBilling: (
    addressWithContact: AddressWithContactModel,
    options?: {cashierMandatoryFields?: CashierMandatoryFieldsOverrides; ignoreEmptyFields?: boolean}
  ) => Promise<boolean>;
}

export interface SubmitFormParams {
  id: string;
  formSchemaId: string;
  formValues?: Record<string, any>;
}

export interface FormDataById {
  errors: FormError[];
}
export interface FormsStoreProps extends FormsStoreObservedProps {
  areFormsSubmitting: boolean;
  areFormsLoaded: boolean;
  onFormNext: (form: SubmitFormParams, nextFormId: string | null) => Promise<void>;
  nextFormIdToFocus: string | null;
  onAdditionalDetailsComplete: (forms: SubmitFormParams[]) => Promise<void>;
  shippingDetailsFieldsProps: FieldPropertiesByTarget | null;
  doesFormHaveNewPhoneAndFullName: boolean;
  dataExtendedFieldsTargets: string[] | null;
  checkoutExcludeGoogleAutoComplete: boolean;
  isFormsLoading: boolean;
  formsDataById: Record<string, FormDataById>;
}

export interface MemberAddressActionBiParams {
  cart: CartModel;
  origin: BiAddressActionOrigin;
  stage: string;
  addressServiceId?: string;
}
export interface MemberStoreProps {
  isMember: boolean;
  addressesInfo: MemberAddressesInfoModel;
  defaultAddressId?: string;
  isAddressesAppInstalled: boolean;
  isMemberAreaAppInstalled: boolean;
  currentUserEmail: string;
  reportEditAddressClicked: (params: MemberAddressActionBiParams) => void;
  reportAddAddressClicked: (params: MemberAddressActionBiParams) => void;
}

export interface CheckboxesStoreProps {
  isPolicyCheckboxChecked: boolean;
  isPaymentPolicyCheckboxChecked?: boolean;
  toggleIsPolicyCheckboxChecked: (checked: boolean) => void;
  toggleIsPaymentPolicyCheckboxChecked: (checked: boolean) => void;
  isDigitalPolicyCheckboxChecked: boolean;
  toggleIsDigitalPolicyCheckboxChecked: (checked: boolean) => void;
  isUnifiedSubscriptionCheckboxChecked: boolean;
  toggleIsUnifiedSubscriptionCheckboxChecked: (checked: boolean) => void;
  isEmailSubscriptionCheckboxChecked: boolean;
  toggleIsEmailSubscriptionCheckboxChecked: (checked: boolean) => void;
  isSmsSubscriptionCheckboxChecked: boolean;
  toggleIsSmsSubscriptionCheckboxChecked: (checked: boolean) => void;
  areCheckoutCheckboxesValid: boolean;
  wasFormSubmitted: boolean;
  setWasFormSubmitted: (wasSubmitted: boolean) => void;
  shouldShowCheckboxesSection: boolean;
  hasLineItemPolicies: boolean;
}

export interface StepsManagerStoreProps {
  updateStepOnStage: (stepIndex: number, stepId: StepId) => void;
  openStep: (stepIndex: number) => void;
  sendEditStepClickedBIEvent: (stepId: StepId, previousStepName?: StepId) => void;
  sendStageExpandedBIEvent: ({
    cart,
    cartSummary,
    stepId,
  }: {
    stepId: StepId;
    cart: CartModel;
    cartSummary?: CartSummaryModel;
  }) => void;
  shouldDisplayExpressCheckout: boolean;
  activeStep: ActiveStep;
  stepsList: CheckoutStep[];
  hasMovedToNextStep: boolean;
  isLeanFlow: boolean;
  shouldShowMemberLoginStep: boolean;
}

export interface NavigationStoreProps {
  clickOnBackToSite: () => void;
  clickOnContinueShopping: () => void;
  clickOnReturnToCart: () => Promise<void>;
  clickOnEditCart: (mobilePosition?: string) => void;
  navigateToThankYouPage: (params: navigateToThankYouPageParams) => Promise<void> | void;
  navigateToExternalUrl: (url: string) => Promise<void> | void;
  continueShoppingUrl?: string;
  editCartUrl: string;
  successUrl?: string;
  redirectBaseUrl?: string;
  trackEvent: (eventName: ITrackEventName, eventProps: ITrackEventParams) => void;
  onLogin: () => Promise<void>;
  onSignup: () => Promise<void>;
  onLogout: () => Promise<void>;
  isSSR: boolean;
  isContinueShoppingEnabled: boolean;
  clickOnRefreshErrorState: () => void;
  isBMPreview: boolean;
  isEditorPreview: boolean;
  isLoginLogoutBarHidden: boolean;
  isExpressButtonsHidden: boolean;
  supportInAppPurchase: boolean;
  areSlotsHidden: boolean;
  isOwnerOrPreview: boolean;
  upgradeUrlWithModalReferral: string;
  shippingSettingsUrl: string;
  paymentMethodsUrl: string;
  isSkipCheckoutRequested: boolean;
}

export interface CheckoutSettingsStoreProps {
  isPremium: boolean;
  ecommerceSettings: EcommerceSettingsModel;
  showCouponSP: boolean;
  doesFastFlowFormHaveFieldsToDisplay: boolean;
  showExpressCheckoutSP: boolean;
  checkoutContent: CheckoutContentModel;
  cannotShip: boolean;
  onBrandOverride?: (brand?: CheckoutBrand) => void;
}

export interface LoadingStateEcommerceSettingsStoreProps {
  loadingStateEcommerceSettings: LoadingStateEcommerceSettingsModel;
}

export interface TranslationsStoreProps {
  _translations: Record<string, string>;
}

export interface SlotStoreProps {
  setSlotsParams: (slotId: SlotId) => void;
  setStepId: (slotId: SlotId, stepId: StepId | null) => void;
  setPaymentId: (slotId: SlotId, paymentId?: PaymentMethod) => void;
  setCheckoutUpdatedDate: (slotId: SlotId, checkoutUpdatedDate?: string) => void;
  setSlotBrand: (slotId: SlotId, isOneColumnView: boolean) => void;
}

export type CouponAndGiftCardBIData = {
  isMember: boolean;
  mobilePosition?: string;
};

export type PlaceOrderUrlParams = {
  errorUrl?: string;
  cancelUrl?: string;
  successUrl?: string;
  pendingUrl?: string;
};

export type navigateToThankYouPageParams = {
  orderId?: string;
};

export type navigateToSuccessPageParams = {
  orderId?: string;
  objectType?: string;
};

export enum FullNameTarget {
  first_name = 'first_name',
  last_name = 'last_name',
}

export enum AddressTarget {
  country = 'country',
  addressLine = 'addressLine',
  addressLine2 = 'addressLine2',
  streetName = 'streetName',
  streetNumber = 'streetNumber',
  postalCode = 'postalCode',
  subdivision = 'subdivision',
  city = 'city',
}

export enum FormHeaderId {
  customer_details_header = '9decce65-bf68-4a14-e170-8242eb363d2b',
  shipping_details_header = '07760e04-b85a-47a7-91e0-ad94fd6c0dd6',
}

export enum FormFieldTarget {
  email = 'email',
  phone = 'phone',
  full_name = 'full_name',
  company_name = 'company_name',
  address = 'address',
  additional_info_1 = 'additional_info_1',
  extendedField = 'user_custom_field',
  vat_id = 'vat_id',
}

export type FormOverrides = CustomerDetailsFormOverrides | BillingFormOverrides;

export interface CustomerDetailsFormValues {
  [FormFieldTarget.email]?: string;
  [FormFieldTarget.full_name]?: {
    [FullNameTarget.first_name]?: string;
    [FullNameTarget.last_name]?: string;
  };
  [FormFieldTarget.company_name]?: string;
  [FormFieldTarget.phone]?: string;
  [FormFieldTarget.additional_info_1]?: string;
  [FormFieldTarget.address]?: {
    [AddressTarget.country]?: string;
    [AddressTarget.city]?: string;
    [AddressTarget.subdivision]?: string;
    [AddressTarget.postalCode]?: string;
    [AddressTarget.addressLine]?: string;
    [AddressTarget.streetName]?: string;
    [AddressTarget.streetNumber]?: string;
    [AddressTarget.addressLine2]?: string;
  };
}

export type FullNameOverrides = {
  [key in FullNameTarget]?: FormOverride;
};

export type FullNameValue = {
  [FullNameTarget.first_name]?: string;
  [FullNameTarget.last_name]?: string;
};

export type AddressOverrides = {
  [key in AddressTarget]?: FormOverride;
};

export interface BillingFormOverrides {
  [FormFieldTarget.full_name]?: FormOverride & FullNameOverrides;
  [FormFieldTarget.phone]?: FormOverride;
  [FormFieldTarget.company_name]?: FormOverride;
  [FormFieldTarget.address]?: AddressOverrides;
  [FormFieldTarget.additional_info_1]?: FormOverride;
  [FormFieldTarget.vat_id]?: FormOverride;
}

export interface CustomerDetailsFormOverrides {
  [FormFieldTarget.email]?: FormOverride;
  [FormFieldTarget.full_name]?: FormOverride & FullNameOverrides;
  [FormFieldTarget.phone]?: FormOverride;
  [FormFieldTarget.company_name]?: FormOverride;
  [FormFieldTarget.address]?: AddressOverrides;
  [FormFieldTarget.additional_info_1]?: FormOverride;
  [FormHeaderId.customer_details_header]?: FormOverride;
  [FormHeaderId.shipping_details_header]?: FormOverride;
}

export enum CustomerDetailsFormSection {
  email = 'email',
  contact = 'contact',
  address = 'address',
  additionalInfo = 'additionalInfo',
  extendedFields = 'extendedFields',
}

export type FieldPropertiesByTarget = NonNullable<ReturnType<FormControllerAPI['getFieldPropertiesByTarget']>>;

export enum StepState {
  EMPTY = 'empty',
  OPEN = 'open',
  COLLAPSED = 'collapsed',
}

export type CheckoutStep = {
  id: StepId;
  state: StepState;
};

export type StepImplementationProps = {
  index?: number;
};

export enum StepId {
  memberLogin = 'member-login',
  customerDetails = 'contact-details',
  additionalDetails = 'additional-details',
  deliveryMethod = 'delivery-method',
  paymentAndPlaceOrder = 'payment-and-place-order',
}

export type ActiveStep = {
  stepId: StepId;
  stepIndex: number;
};

export enum SummaryLocation {
  checkoutApp = 'checkoutApp',
  paymentAndPlaceOrderStep = 'paymentAndPlaceOrderStep',
}

export enum clickOnShowMoreBi {
  OPEN = 'open',
  CLOSE = 'close',
}

export enum clickOnToggleBi {
  CHECK = 'check',
  UNCHECK = 'uncheck',
}

export interface DeliveryStoreProps {
  hasSelectedOption: boolean;
  componentState: ComponentState;
}

export type getCheckoutContentApplicationErrors = ErrorCodesMapConditionalEnforcement<
  HttpResponse<GetCheckoutContentResponse & {__applicationErrorsType?: GetCheckoutContentApplicationErrors}>
>;
export type getOrderPaymentRequestApplicationErrors = ErrorCodesMapConditionalEnforcement<
  HttpResponse<GetOrderPaymentRequestResponse & {__applicationErrorsType?: GetOrderPaymentRequestApplicationErrors}>
>;
export type chargeOrderPaymentRequestApplicationErrors = ErrorCodesMapConditionalEnforcement<
  HttpResponse<
    ChargeOrderPaymentRequestResponse & {__applicationErrorsType?: ChargeOrderPaymentRequestApplicationErrors}
  >
>;

export type ModelContext = {
  flowAPI: ControllerParams['flowAPI'];
  useConvertedPrice: boolean;
  pricesIncludeTax: boolean;
  formatAmount: (amount: number | string) => string;
  localeKeys: ILocaleKeys;
};
